import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@jeraisonne/db';
import type { TutorAssignmentSummary, AssignmentItem } from './objective-types';
import { withDoneFromAttempts } from './assignment-done';

/**
 * Retourne un résumé par tuteur particulier actif de l'élève (migration
 * 0227 — un élève peut avoir plusieurs tuteurs, un par matière ou un tuteur
 * pour plusieurs matières). Un `TutorAssignmentSummary` par lien `active`.
 */
export async function getTutorAssignments(
  supabase: SupabaseClient<Database>,
  studentId: string,
): Promise<TutorAssignmentSummary[]> {
  const { data: tutorLinks } = await supabase
    .from('tutor_links')
    .select(
      'tutor_id, subjects, profiles!tutor_links_tutor_id_fkey ( display_name:visible_name, avatar_url )',
    )
    .eq('student_id', studentId)
    .eq('status', 'active');

  if (!tutorLinks || tutorLinks.length === 0) return [];

  return Promise.all(
    tutorLinks.map(async (tutorLink) => {
      const tutor = tutorLink.profiles as unknown as {
        display_name: string;
        avatar_url: string | null;
      } | null;
      const tutorId = tutorLink.tutor_id;
      const tutorName = tutor?.display_name ?? 'Professeur';
      const tutorAvatarUrl = tutor?.avatar_url ?? null;
      const subjects = tutorLink.subjects;

      // Individual assignments (no class) from this tutor
      const { data: assignments } = await supabase
        .from('assignments')
        .select(
          `
          id, exercise_id, status, due_at, assigned_at,
          exercises ( title, difficulty, estimated_minutes )
        `,
        )
        .eq('student_id', studentId)
        .eq('tutor_id', tutorId)
        .is('class_id', null);

      const raw: AssignmentItem[] = (assignments ?? []).map((a) => {
        const ex = a.exercises as unknown as {
          title: string;
          difficulty: string;
          estimated_minutes: number;
        };
        return {
          id: a.id,
          exerciseId: a.exercise_id,
          exerciseTitle: ex.title,
          difficulty: ex.difficulty as AssignmentItem['difficulty'],
          estimatedMinutes: ex.estimated_minutes,
          status: a.status as AssignmentItem['status'],
          dueAt: a.due_at ?? null,
          assignedAt: (a as { assigned_at?: string | null }).assigned_at ?? null,
        };
      });
      // `finish_attempt` ne met pas le devoir à `completed` : on le déduit des tentatives.
      const items = await withDoneFromAttempts(supabase, studentId, raw);

      return {
        tutorId,
        tutorName,
        tutorAvatarUrl,
        subjects,
        pendingCount: items.filter((a) => a.status === 'pending').length,
        inProgressCount: items.filter((a) => a.status === 'in_progress').length,
        completedCount: items.filter((a) => a.status === 'completed').length,
        assignments: items,
      };
    }),
  );
}
